import { Link } from "@tanstack/react-router";
import { Layout } from "@/components/layout/Layout";
import { TemplateCard } from "@/components/template/TemplateCard";
import { ThumbnailWarmer } from "@/components/template/ThumbnailWarmer";
import { useTemplatesQuery } from "@/api/templates";
import { useRendersQuery } from "@/api/renders";
import type { RenderJob } from "@/types/render";

function RecentRenderRow({ job }: { job: RenderJob }) {
  const statusColors: Record<RenderJob["status"], string> = {
    queued: "text-warning",
    rendering: "text-accent",
    complete: "text-success",
    error: "text-danger",
  };

  return (
    <Link
      to="/renders/$id"
      params={{ id: job.id }}
      className="flex items-center justify-between rounded-md px-3 py-2 transition-colors hover:bg-surface-3"
    >
      <div>
        <span className="text-sm text-ink">
          {job.template_name ?? job.template_id}
        </span>
        <span className="ml-2 text-xs text-ink-3">
          {job.format}
        </span>
      </div>
      <span className={`text-xs font-medium ${statusColors[job.status]}`}>
        {job.status}
      </span>
    </Link>
  );
}

export function DashboardPage() {
  const { data: templates } = useTemplatesQuery({ limit: 4 });
  const { data: renders } = useRendersQuery({ limit: 5 });

  return (
    <Layout>
      {/* Background warmer fills missing thumbnails for owned templates
          one at a time. Renders nothing visible — runs in the corner
          until done. */}
      <ThumbnailWarmer templates={templates} />
      <div className="mx-auto max-w-6xl p-6">
        {/* Hero */}
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-ink">
              Dashboard
            </h1>
            <p className="mt-1 text-sm text-ink-2">
              Create, preview, and render motion graphics templates
            </p>
          </div>
          <Link
            to="/create"
            className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover"
          >
            + Create Template
          </Link>
        </div>

        {/* Recent Templates */}
        <section className="mb-8">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-ink">
              Recent Templates
            </h2>
            <Link
              to="/templates"
              className="text-xs font-medium text-accent hover:underline"
            >
              View All
            </Link>
          </div>
          {templates && templates.length > 0 ? (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {templates.map((t) => (
                <TemplateCard key={t.id} template={t} />
              ))}
            </div>
          ) : (
            <div className="rounded-xl border border-line bg-surface-2 py-12 text-center">
              <p className="text-sm text-ink-3">
                No templates yet.
              </p>
              <Link
                to="/create"
                className="mt-2 inline-block text-sm font-medium text-accent hover:underline"
              >
                Create your first template
              </Link>
            </div>
          )}
        </section>

        {/* Recent Renders */}
        <section>
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-ink">
              Recent Renders
            </h2>
            <Link
              to="/renders"
              className="text-xs font-medium text-accent hover:underline"
            >
              View All
            </Link>
          </div>
          {renders && renders.length > 0 ? (
            <div className="rounded-xl border border-line bg-surface-2">
              <div className="divide-y divide-line">
                {renders.map((job) => (
                  <RecentRenderRow key={job.id} job={job} />
                ))}
              </div>
            </div>
          ) : (
            <div className="rounded-xl border border-line bg-surface-2 py-12 text-center text-sm text-ink-3">
              No render jobs yet.
            </div>
          )}
        </section>
      </div>
    </Layout>
  );
}
